import React from "react";
import PageHero from "../components/PageHero";
import TimelineItem from "../components/TimelineItem";
import Metrics from "../components/Metrics";
import Footer from "../components/Footer";
import { Clock, Package, Leaf } from "lucide-react";

const timeline = [
  {
    year: "Mar 2021",
    title: "The PS4 That Never Was",
    body:
      "I had saved 50,000 LKR for my birthday with one goal: buy a PlayStation 4. But then I stumbled upon a YouTube video about 3D printing. The possibilities fascinated me so much that I made a spontaneous decision—I bought a 3D printer instead. Little did I know this impulse would change everything.",
    images: ["/images/3d_printer.jpeg", "/images/3d_printer_box.jpg"],
    badge: "Origin",
  },
  {
    year: "Mar 2021",
    title: "A Gift of Light",
    body:
      "End of March marked my second anniversary with my girlfriend (now my wife). I hadn't bought anything for her, so I researched what I could create with my new printer. I discovered lithophanes. After several prototypes, I crafted our first night lamp with LEDs, batteries, and a switch. When we posted it on social media to celebrate, people started asking if they could have one too.",
    images: ["/images/prototyping.jpg", "/images/2nd_anni_gift.png"],
    badge: "Love",
  },
  {
    year: "Apr 2021",
    title: "Official Launch on Dad's Birthday",
    body:
      "April 21st, 2021—my father's birthday. I officially launched Merch Masters LK as a side hustle. I gathered materials, set up social media accounts, and sent free samples to my closest friends. What started as a spontaneous gift became a business born from passion and curiosity.",
    images: ["/images/logo.jpg"],
    badge: "Launch",
  },
  {
    year: "2022-2023",
    title: "Growth & Community",
    body:
      "Orders started flowing in steadily. We refined our process, upgraded the lamp experience, and started shipping beyond Sri Lanka. Attending events gave us valuable feedback and connected us with amazing customers who shared their stories of how our lamps became part of their memories.",
    images: ["/images/IMG-20241214-WA0051.jpg","/images/IMG-20241214-WA0037.jpg"],
    badge: "Growth",
  },
  {
    year: "2024-2025",
    title: "800+ Orders & Going International",
    body:
      "Four years later, we've delivered over 800 lithophane lamps locally and completed 20+ international orders. We've continuously improved quality, explored new designs, and now we're planning to bring exciting new products to market. This is just the beginning.",
    images: ["/images/Hero_Images/31.jpg"],
    badge: "Milestone",
  },
];

const metrics = [
  { label: "Orders fulfilled", value: "800+" },
  { label: "International orders", value: "20+" },
  { label: "Years in business", value: "4" },
  { label: "Happy customers", value: "800+" },
];

const values = [
  {
    title: "Handcrafted with Care",
    body:
      "Every lithophane is 3D printed with precision and hand-finished. We personally check each lamp before it reaches you, ensuring your memories are preserved beautifully.",
    icon: <Clock className="w-8 h-8" />,
  },
  {
    title: "Made-to-Order Quality",
    body:
      "We don't mass-produce. Each lamp is created specifically for you with your chosen photo, allowing us to focus on quality over quantity.",
    icon: <Package className="w-8 h-8" />,
  },
  {
    title: "Sustainable Materials",
    body:
      "Our lamps are made with PLA+, a bio-based polymer that's environmentally friendly. We believe in creating memories that don't cost the earth.",
    icon: <Leaf className="w-8 h-8" />,
  },
];

const OurStory = () => {
  return (
    <main className="bg-[#F5F0EB]">
      <PageHero title="Our Story" />

      {/* Founding Story */}
      <section className="py-20 px-6 max-w-screen-xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8 items-center bg-white rounded-2xl p-8 shadow-lg">
          <div className="order-2 md:order-1">
            <h2 className="text-3xl font-extrabold mb-4 text-gray-900">How It All Started</h2>
            <p className="text-[15px] leading-7 text-gray-600">
              March 2021. I had saved 50,000 LKR for my birthday with one dream: to finally own a PlayStation 4. I had the money. I was ready. Then, scrolling through YouTube, I discovered something that changed everything—a video about 3D printing. The idea of creating anything I could imagine fascinated me so deeply that I made an impulsive decision: I bought a 3D printer instead of the PS4.
            </p>
            <p className="text-[15px] leading-7 text-gray-600 mt-4">
              For weeks, I printed everything I could think of, experimenting with designs and learning the craft. Then came our second anniversary. I hadn't bought a gift for my girlfriend (now my wife), so I searched for something meaningful I could create. That's when I found lithophanes. After several prototypes and many late nights, I crafted our first night lamp, complete with LEDs, batteries, and a switch. When we shared it on social media, the response was unexpected—people wanted one too.
            </p>
            <blockquote className="mt-4 text-lg font-semibold text-[#E85D44] border-l-4 border-[#FFD93D] pl-4">
              "What started as a gift became a business born from love and curiosity."
            </blockquote>
            <div className="mt-6 grid grid-cols-3 gap-4 text-center text-sm font-bold text-gray-700">
              <div>1 printer</div>
              <div>1 passion</div>
              <div>800+ memories</div>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="rounded-2xl overflow-hidden shadow-lg" style={{ aspectRatio: '1/1' }}>
              <iframe
                className="w-full h-full border-0"
                src="https://www.youtube.com/embed/DbAUyhGz8HI?autoplay=1&mute=1&loop=1&playlist=DbAUyhGz8HI&controls=0&modestbranding=1&rel=0&fs=0&disablekb=1&iv_load_policy=3&playsinline=1&showinfo=0"
                title="First lithophane night lamp"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-screen-xl mx-auto px-6 space-y-16">
        {timeline.map((item, i) => (
          <TimelineItem key={item.year} item={item} index={i} />
        ))}
      </section>

      {/* Metrics */}
      <section className="py-20 px-6 max-w-screen-xl mx-auto">
        <Metrics items={metrics} />
      </section>

      {/* Values */}
      <section className="py-20 px-6 max-w-screen-xl mx-auto grid md:grid-cols-3 gap-8">
        {values.map((v, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-200">
            <div className="text-[#E85D44]">{v.icon}</div>
            <h3 className="mt-4 text-xl font-extrabold text-gray-900">{v.title}</h3>
            <p className="mt-2 text-[15px] leading-7 text-gray-600">{v.body}</p>
            <a href="/policy" className="mt-4 inline-block text-sm text-[#E85D44] hover:text-[#8A7255] underline transition-colors">
              Learn more
            </a>
          </div>
        ))}
      </section>

      {/* Where We Are Now */}
      <section className="py-20 px-6 max-w-screen-xl mx-auto grid md:grid-cols-2 gap-8 items-center">
        <img
          src="/images/IMG-20250912-WA0029.jpeg"
          alt="Current lithophane production"
          className="rounded-2xl w-full h-full object-cover shadow-lg"
        />
        <div>
          <h2 className="text-3xl font-extrabold mb-4 text-gray-900">Where We Are Now</h2>
          <p className="text-[15px] leading-7 text-gray-600">
            Today, Merch Masters LK continues as my passionate side hustle. What began on April 21st, 2021—my father's birthday—has grown into something beautiful. We've completed over 800 orders across Sri Lanka and shipped 20+ orders internationally, turning cherished photos into glowing memories.
          </p>
          <p className="text-[15px] leading-7 text-gray-600 mt-4">
            We've attended local events, gathered invaluable feedback, and continuously improved the lithophane experience. Each lamp is still made with the same care and attention as that first anniversary gift. And we're not stopping here—new products are in development, ready to bring more innovative ways to preserve your memories.
          </p>
          <div className="mt-6 space-y-2 text-sm text-gray-700 border-l-4 border-[#FFD93D] pl-4">
            <p className="font-semibold">This is just the beginning.</p>
            <p className="italic">Four years of turning moments into memories, one lamp at a time.</p>
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="bg-gradient-to-br from-[#E85D44] to-[#D44D34] py-16">
        <div className="max-w-screen-xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-2">Ready to Create Your Memory?</h3>
            <p className="text-white/90">Turn your favorite photo into a glowing lithophane lamp</p>
          </div>
          <div className="flex gap-4">
            <a
              className="h-12 px-8 rounded-full bg-[#FFD93D] hover:bg-[#8A7255] text-black font-semibold flex items-center transition-all duration-300 shadow-lg hover:shadow-xl uppercase tracking-wider text-sm"
              href="/3d-night-lamp"
            >
              Learn More
            </a>
            <a
              className="h-12 px-8 rounded-full border-[1.5px] border-white text-white hover:bg-white hover:text-[#E85D44] flex items-center transition-all duration-300 font-semibold uppercase tracking-wider text-sm"
              href="/design"
            >
              Design Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default OurStory;